import React, { FC, useState, useEffect, useCallback } from 'react'
import clsx from 'clsx'
import { useQuery } from '@tanstack/react-query'
import get from 'lodash.get'

import { ASSIGNMENT_STATUS } from '@pepper/utils/src/constant/enum/assignment_status'
import {
  Spin,
  useShowMessage,
  useFeedbackDetails,
  useGlobalState,
} from '@pepper/ui'

/** apis */
import {
  fetchThreads,
  makeThreadSeen,
} from '@pepper/utils/src/api/assignments/assignments'

import RecentActivity from 'components/RecentActivity/RecentActivity'
import { useSelectedCompanyId } from 'utils/functions/misc'

import styles from './_.module.css'

interface Props {
  projectFormFields: BackendFormField[]
  assignmentBrief: BackendFormField[]
  id: string
  data: Assignment
  refetch: () => void
  callBacks?: {
    setTotalRecentActivity: (value: number) => void
  }
  keepSamePage: boolean
}

export interface Activity
  extends Partial<ThreadMessage>,
    Partial<Timeline>,
    Partial<ClientFeedback> {
  activityType?: 'thread' | 'log' | 'feedback'
}

const Activities: FC<Props> = ({
  id,
  data: assignmentDetails,
  refetch: refetchAssignmentDetails,
  callBacks,
  keepSamePage,
  projectFormFields,
  assignmentBrief,
}) => {
  const { setInboxThreadUpdate } = useGlobalState()
  const showMessage = useShowMessage()
  const [activities, setActivities] = useState<Activity[]>([])
  const companyId = useSelectedCompanyId()
  const [latestAssignmentStatus, setAssignmentStatus] = useState<boolean>(false)
  const projectCategory = assignmentDetails.category?.key ?? 'text'

  const {
    isLoading,
    data: comments,
    refetch: refetchThreads,
  } = useQuery<ThreadBusiness[]>({
    queryKey: ['threads', id],
    queryFn: async () => {
      const { status, data, error } = await fetchThreads({
        id,
        type: 'deadline,rework,brief,other,activity',
      })

      if (status === 'error') {
        showMessage({ type: 'error', message: error })
      }

      return data
    },
  })

  const { feedbacks, refetchFeedbacks } = useFeedbackDetails(
    assignmentDetails.id,
    { company_id: companyId }
  )

  const getLatestAssignmentUnreadStatus = (
    assignmentActivity: ThreadMessage[]
  ) => {
    try {
      if (!assignmentActivity || assignmentActivity.length <= 0) {
        return false
      }
      assignmentActivity.sort((oldAssignment, newAssignment) => {
        const oldAssignmentTimestamp = oldAssignment.timestamp
          ? new Date(oldAssignment.timestamp).getTime()
          : new Date().getTime()
        const newAssignmentTimestamp = newAssignment.timestamp
          ? new Date(newAssignment.timestamp).getTime()
          : new Date().getTime()
        return newAssignmentTimestamp > oldAssignmentTimestamp ? 1 : -1
      })
      return assignmentActivity[0].unread
    } catch (error) {
      return false
    }
  }

  const updateReadStats = useCallback(async () => {
    const res = await makeThreadSeen({ assignmentId: id })
    if (res?.data?.status === 'success') {
      setInboxThreadUpdate(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    callBacks?.setTotalRecentActivity?.(activities.length)
  }, [callBacks, activities.length])

  useEffect(() => {
    if (comments && assignmentDetails?.timeline && feedbacks) {
      // Threads
      let activitySet: ThreadMessage[] = []
      const onlyComments = comments.filter(item => {
        if (item.thread_type !== 'activity') {
          return item
        } else {
          activitySet = [...activitySet, ...item.messages]
        }
        return null
      })
      const latestAssignmentReadStatus =
        getLatestAssignmentUnreadStatus(activitySet)
      latestAssignmentReadStatus &&
        setAssignmentStatus(latestAssignmentReadStatus)
      const _messages: ThreadMessage[] = onlyComments.reduce(
        (_threads: ThreadMessage[], value: ThreadBusiness) => {
          return [..._threads, ...value.messages]
        },
        []
      )

      // timeline
      let _latestSubmission = false
      const _timelines = assignmentDetails.timeline
        .filter(
          timeline =>
            [
              ASSIGNMENT_STATUS.CLI_AWA.id,
              ASSIGNMENT_STATUS.CLI_RWK.id,
              ASSIGNMENT_STATUS.CLI_RJT.id,
              ASSIGNMENT_STATUS.CLI_FIN.id,
              ASSIGNMENT_STATUS.WRI_WRK.id,
              ASSIGNMENT_STATUS.HOLD.id,
              ASSIGNMENT_STATUS.SCRAPPED.id,
            ].includes(timeline.status_code) ||
            timeline.is_resolved ||
            timeline.is_unscrapped ||
            timeline.changes
        )
        .reduce((timeline: Timeline[], value: Timeline) => {
          if (
            [
              ASSIGNMENT_STATUS.CLI_AWA.id,
              ASSIGNMENT_STATUS.CLI_FIN.id,
            ].includes(value.status_code) &&
            !_latestSubmission
          ) {
            _latestSubmission = true
            timeline.push({ ...value, isLatest: true })
          } else {
            timeline.push({ ...value, isLatest: false })
          }
          return timeline
        }, [])

      // feedbacks
      const _feedbacks = feedbacks.client_feedback
        ? [...feedbacks.client_feedback]
        : []
      //** */
      // set activity type
      const _activities: Activity[] = [
        ..._messages,
        ..._timelines,
        ..._feedbacks,
      ].map((data: Activity) => ({
        ...data,
        activityType: (() => {
          if (data?.timestamp) return 'thread'
          else if (data?.created_at) return 'log'
          else return 'feedback'
        })(),
      }))

      // sort activity by it's created date
      _activities.sort((a, b) => {
        const dateA = a.timestamp || a.created_at || a.date_created
        const dateB = b.timestamp || b.created_at || b.date_created
        const _timestampA = dateA
          ? new Date(dateA).getTime()
          : new Date().getTime()
        const _timestampB = dateB
          ? new Date(dateB).getTime()
          : new Date().getTime()

        if (_timestampA < _timestampB) {
          return -1
        }
        if (_timestampA > _timestampB) {
          return 1
        }
        return 0
      })
      setActivities(_activities)
      updateReadStats()
    }
  }, [
    comments,
    assignmentDetails.timeline,
    feedbacks,
    updateReadStats,
    assignmentDetails.writer_allotted,
  ])

  return (
    <>
      <section className={styles.activityWrap}>
        {isLoading ? (
          <div className={styles.spinWrap}>
            <Spin />
          </div>
        ) : (
          <section
            className={clsx(styles.commentSection, {
              [styles.commentSectionNoSidebar]: keepSamePage,
            })}
          >
            <RecentActivity
              projectFormFields={projectFormFields}
              assignmentBrief={assignmentBrief}
              projectCategory={projectCategory}
              keepSamePage={keepSamePage}
              latestAssignmentStatus={latestAssignmentStatus}
              activities={activities}
              refetch={{
                threads: refetchThreads,
                assignmentDetails: refetchAssignmentDetails,
                feedback: refetchFeedbacks,
              }}
              assignmentId={id}
              disabled={true} // Disabling
              extraData={{
                activeStatus: assignmentDetails.status,
                assignmentTitle: assignmentDetails.assignment_title ?? '',
                assignmentCategory: assignmentDetails.category?.key ?? 'text',
                submissionFiles:
                  assignmentDetails?.submission_file?.extras ?? [],
                fileSrcType: assignmentDetails?.submission_file?.src_type,
                googleDocFiles: [
                  {
                    fileUrl: assignmentDetails?.submission_file?.file_url ?? '',
                  },
                ],
                show_pepper_creator: assignmentDetails?.show_pepper_creator,
              }}
              submissionText={get(
                assignmentDetails,
                'submission_file.content',
                null
              )}
              hide={true}
            />
          </section>
        )}
      </section>
    </>
  )
}

export default Activities
